/** Connect tab (the /network index route, #310): everything a member needs to
 *  get ON the network — the tenant Wi-Fi, their personal 802.1X sign-in, and
 *  the network's facts. Performance and device management live in their own
 *  tabs. */
import { Link } from "react-router-dom";
import { Wifi } from "lucide-react";
import { Button, Card, Divider, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { useAuthStore } from "@/lib/auth";
import { Dot1xCredentialCard } from "@/components/Dot1xCredentialCard";
import { ChangeWifiPassword, WiFiNetworkCard } from "@/components/network/wifi-card";
import { NetworkInfoCard } from "@/components/network/network-info-card";
import { NotAssigned } from "@/components/network/not-assigned";
import { PlanCard } from "@/components/network/plan-card";
import { errorStatus, supportLink } from "@/components/network/shared";
import { useNetworkInfo, useWirelessInfo } from "@/hooks/use-network-queries";

export default function NetworkConnectTab() {
  const { user } = useAuthStore();

  const wifiQuery = useWirelessInfo();
  const netQuery = useNetworkInfo();

  // A 404 means "not assigned yet" — a normal state with a friendly empty card,
  // distinct from a real failure (which keeps a red error).
  const wifi404 = errorStatus(wifiQuery.error) === 404;
  const net404 = errorStatus(netQuery.error) === 404;
  const wifiError = wifiQuery.isError && !wifi404;
  const netError = netQuery.isError && !net404;

  const wireless = wifiQuery.data;
  const networkData = netQuery.data;
  const isAdmin = !!user?.is_tenant_admin;

  const loading = wifiQuery.isLoading || netQuery.isLoading;
  const nothingAssigned = wifi404 && net404;

  if (loading) {
    return <Text fz="sm" c="dimmed">Loading...</Text>;
  }

  // Nothing assigned at all — one combined, friendly empty state.
  if (nothingAssigned) {
    return (
      <NotAssigned
        title="No network or WiFi yet"
        body="Your space hasn't been assigned its own network or WiFi yet. Get in touch and we'll get you connected."
        subject="Missing network and WiFi"
        message="Hi! Our space doesn't have a network or WiFi assigned yet. Could you help get us set up?"
      />
    );
  }

  return (
    <Stack gap="lg">
      {/* Two explicit columns rather than four cells flowing through the
          grid. Every card here hides itself when it does not apply —
          Dot1x without an 802.1X network, NetworkInfo without a prefix —
          and with row-flow that reassigns which column the rest land in,
          so the order only held for members who happened to have all of
          them. A Stack per column keeps it true for everyone. */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" verticalSpacing="md">
        <Stack gap="md">
          {/* Personal 802.1X sign-in leads: it is the member's OWN
              credential and the thing they came to this tab for. The
              shared keys below are the fallback for devices that cannot
              do 802.1X. Hides itself entirely on sites without one. */}
          <Dot1xCredentialCard />

          <Card withBorder padding="lg">
            <Group gap="xs" mb="sm">
              <Wifi size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
              <Text fw={500}>WiFi</Text>
            </Group>
            <Stack gap="md">
              {wifiError ? (
                <Text fz="sm" c="red">Couldn't load your WiFi details. Please try again.</Text>
              ) : wifi404 ? (
                <Stack align="center" gap="sm" py="md">
                  <Text fz="lg" fw={500} ta="center">No WiFi yet</Text>
                  <Text fz="md" c="dimmed" ta="center">
                    Your space hasn't been assigned its own WiFi yet.
                  </Text>
                  <Button
                    component={Link}
                    to={supportLink(
                      "Missing WiFi",
                      "Hi! Our space doesn't have a WiFi network assigned yet. Could you help get us set up?",
                    )}
                    size="sm"
                    mt="xs"
                  >
                    Contact support
                  </Button>
                </Stack>
              ) : wireless?.guest_ssid ? (
                <>
                  <WiFiNetworkCard
                    ssid={wireless.guest_ssid}
                    password={wireless.guest_password}
                    label="Guest"
                    showPassword={true}
                  />
                  {wireless.ssid && (
                    <>
                      <Divider />
                      <WiFiNetworkCard
                        ssid={wireless.ssid}
                        password={wireless.password}
                        label="Your Network"
                        showPassword={isAdmin}
                      />
                    </>
                  )}
                </>
              ) : wireless?.ssid ? (
                <WiFiNetworkCard
                  ssid={wireless.ssid}
                  password={wireless.password}
                  label="Your Network"
                  showPassword={isAdmin}
                />
              ) : (
                <Text fz="sm" c="dimmed">No WiFi networks configured.</Text>
              )}
              {isAdmin && wireless?.ssid && (
                <>
                  <Divider />
                  <ChangeWifiPassword />
                </>
              )}
            </Stack>
          </Card>
        </Stack>

        <Stack gap="md">
          {/* Network Information answers "what IS my network" — reference
              detail, not something to act on, so it sits opposite rather
              than above. Rendered only once the data is there; the tab's
              own error / no-network branches below say what happened
              otherwise. */}
          {networkData && <NetworkInfoCard network={networkData} />}

          {/* The plan ladder (#313) — every tier with its price, the
              member's own marked. Deliberately HERE and not beside the
              capacity card: an always-present ladder there would be the
              permanent upsell banner the capacity spec decided against. */}
          <PlanCard hasNetwork={!net404} />
        </Stack>
      </SimpleGrid>

      {netError ? (
        <Card withBorder padding="lg">
          <Text fz="sm" c="red">Couldn't load your network information. Please try again.</Text>
        </Card>
      ) : net404 ? (
        <NotAssigned
          title="No network yet"
          body="Your space hasn't been assigned its own network yet. Get in touch and we'll get you connected."
          subject="Missing network"
          message="Hi! Our space doesn't have a network assigned yet. Could you help get us set up?"
        />
      ) : null}
    </Stack>
  );
}
